import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useBilling } from "@/components/BillingProvider";

export default function BillingSuccess() {
  const [params] = useSearchParams();
  // PayPal returns ?token=ORDERID (and optionally ?PayerID=...). We also pass ?txn=... ourselves.
  const sessionId = params.get("token") || params.get("session_id");
  const navigate = useNavigate();
  const { refresh } = useBilling();
  const [state, setState] = useState("checking"); // checking | paid | failed
  const [planId, setPlanId] = useState(null);

  useEffect(() => {
    let attempts = 0;
    let cancelled = false;

    const poll = async () => {
      if (cancelled || !sessionId) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/billing/checkout/status/${sessionId}`);
        setPlanId(data.plan_id);
        if (data.payment_status === "paid") {
          setState("paid");
          await refresh();
          return;
        }
        if (data.status === "expired") {
          setState("failed");
          return;
        }
      } catch {/* swallow */}
      if (attempts >= 10) { setState("failed"); return; }
      setTimeout(poll, 2000);
    };

    if (sessionId) poll();
    else setState("failed");
    return () => { cancelled = true; };
  }, [sessionId, refresh]);

  return (
    <div className="min-h-screen grid place-items-center relative z-10 p-6" data-testid="billing-success">
      <div className="glass-panel rounded-3xl p-10 max-w-md w-full text-center">
        {state === "checking" && (
          <>
            <Loader2 className="h-10 w-10 text-blue-500 mx-auto animate-spin" />
            <h2 className="font-display text-2xl font-semibold mt-4">Confirmando tu pago…</h2>
            <p className="text-sm text-muted-foreground mt-2">Esto toma unos segundos.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto" />
            <h2 className="font-display text-2xl font-semibold mt-4">¡Pago confirmado!</h2>
            <p className="text-sm text-muted-foreground mt-2">Tu plan está activo. Bienvenido a Sprintroll Pro.</p>
            <Button onClick={() => navigate("/")} className="mt-6 w-full bg-blue-600 hover:bg-blue-700 text-white" data-testid="billing-success-home">
              Ir al Dashboard
            </Button>
          </>
        )}
        {state === "failed" && (
          <>
            <XCircle className="h-12 w-12 text-rose-500 mx-auto" />
            <h2 className="font-display text-2xl font-semibold mt-4">No pudimos confirmar el pago</h2>
            <p className="text-sm text-muted-foreground mt-2">Vuelve a intentarlo desde la página de precios.</p>
            <Button onClick={() => navigate("/pricing")} className="mt-6 w-full bg-blue-600 hover:bg-blue-700 text-white" data-testid="billing-success-retry">
              Volver a planes
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
